import React, {
	createContext, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import { Button, Flex, Stack } from '@chakra-ui/react';

const LEFT_SEPARATOR = 0;
const RIGHT_SEPARATOR = -1;

const unique = (values) => [...new Set(values)];

export function generatePages({
	pagesQuantity, currentPage, innerLimit, outerLimit,
}) {
	if (!pagesQuantity) return [];

	const pages = Array.from({ length: pagesQuantity }, (_, index) => index + 1);
	if (!innerLimit || !outerLimit) return pages;

	const outerLeft = pages.slice(0, outerLimit);
	const outerRight = pages.slice(1).slice(-outerLimit);
	const outerLeftEnd = outerLeft[outerLeft.length - 1];
	const outerRightStart = outerRight[0];

	const innerLeftStart = currentPage - innerLimit;
	const innerLeft = innerLeftStart < outerLeftEnd
		? pages.slice(outerLeftEnd, currentPage - 1)
		: pages.slice(innerLeftStart - 1, currentPage - 1);
	const hasLeftGap = innerLeft[0] > outerLeftEnd + 1;

	const innerRightEnd = currentPage + innerLimit;
	const innerRight = innerRightEnd > outerRightStart
		? pages.slice(currentPage + 1, outerRightStart)
		: pages.slice(currentPage, innerRightEnd);
	const hasRightGap = innerRight[innerRight.length - 1] < outerRightStart - 1;

	return unique([
		...outerLeft,
		...innerLeft,
		currentPage,
		...innerRight,
		...outerRight,
	]).flatMap((page) => {
		if (page === outerLeftEnd && hasLeftGap) return [page, LEFT_SEPARATOR];
		if (page === outerRightStart && hasRightGap) return [RIGHT_SEPARATOR, page];
		return [page];
	});
}

const PaginatorContext = createContext(null);

export function usePaginator({ total, initialState }) {
	const [currentPage, setCurrentPage] = useState(initialState.currentPage);
	const [pageSize, setPageSize] = useState(initialState.pageSize ?? 0);
	const [isDisabled, setIsDisabled] = useState(initialState.isDisabled ?? false);

	const offset = pageSize ? currentPage * pageSize - pageSize : 0;
	const pagesQuantity = total && pageSize ? Math.ceil(total / pageSize) : 0;

	return {
		currentPage,
		isDisabled,
		offset,
		pageSize,
		pagesQuantity,
		setCurrentPage,
		setIsDisabled,
		setPageSize,
	};
}

export function Paginator({
	activeStyles = {}, children, currentPage = 1, innerLimit = 0, isDisabled = false,
	normalStyles = {}, onPageChange, outerLimit = 0, pagesQuantity,
}) {
	const [activePage, setActivePage] = useState(1);

	useEffect(() => {
		const validPage = Number.isInteger(currentPage)
			&& currentPage >= 1
			&& pagesQuantity
			&& currentPage <= pagesQuantity;
		if (validPage) setActivePage(currentPage);
	}, [currentPage, pagesQuantity]);

	const changePage = useCallback((page) => {
		if (!isDisabled) {
			setActivePage(page);
			onPageChange(page);
		}
	}, [isDisabled, onPageChange]);

	const value = useMemo(() => ({
		activeStyles,
		changePage,
		currentPage: activePage,
		innerLimit,
		isDisabled,
		normalStyles,
		outerLimit,
		pagesQuantity,
	}), [
		activePage, activeStyles, changePage, innerLimit, isDisabled, normalStyles,
		outerLimit, pagesQuantity,
	]);

	return <PaginatorContext.Provider value={value}>{children}</PaginatorContext.Provider>;
}

function usePaginatorContext() {
	const context = useContext(PaginatorContext);
	if (!context) throw new Error('Paginator components must be rendered inside Paginator');
	return context;
}

export function Previous({ children, ...props }) {
	const {
		changePage, currentPage, isDisabled,
	} = usePaginatorContext();
	const disabled = currentPage === 1 || isDisabled;

	return (
		<Button
			aria-label="Previous page"
			isDisabled={disabled}
			onClick={() => changePage(currentPage - 1)}
			{...props}
		>
			{children}
		</Button>
	);
}

export function Next({ children, ...props }) {
	const {
		changePage, currentPage, isDisabled, pagesQuantity,
	} = usePaginatorContext();
	const disabled = !pagesQuantity || currentPage >= pagesQuantity || isDisabled;

	return (
		<Button
			aria-label="Next page"
			isDisabled={disabled}
			onClick={() => changePage(currentPage + 1)}
			{...props}
		>
			{children}
		</Button>
	);
}

function Separator({ direction }) {
	const {
		changePage, currentPage, innerLimit, isDisabled,
	} = usePaginatorContext();
	const backwards = direction === 'left';
	const targetPage = backwards ? currentPage - innerLimit : currentPage + innerLimit;

	return (
		<Flex as="li">
			<Button
				aria-label={`Jump pages ${backwards ? 'backwards' : 'forward'}`}
				bg="transparent"
				cursor="pointer"
				isDisabled={isDisabled}
				minW="auto"
				onClick={() => changePage(targetPage)}
				px={1}
			>
				…
			</Button>
		</Flex>
	);
}

function Page({ page }) {
	const {
		activeStyles, changePage, currentPage, isDisabled, normalStyles,
	} = usePaginatorContext();

	if (page === LEFT_SEPARATOR) return <Separator direction="left" />;
	if (page === RIGHT_SEPARATOR) return <Separator direction="right" />;

	const active = page === currentPage;
	return (
		<Flex as="li">
			<Button
				aria-current={active ? 'page' : undefined}
				aria-label={active ? `Current page, page ${page}` : `Go to page ${page}`}
				cursor="pointer"
				isDisabled={isDisabled}
				minW="auto"
				onClick={() => changePage(page)}
				px={1}
				{...(active ? activeStyles : normalStyles)}
			>
				{page}
			</Button>
		</Flex>
	);
}

export function PageGroup(props) {
	const {
		currentPage, innerLimit, outerLimit, pagesQuantity,
	} = usePaginatorContext();
	const pages = useMemo(() => generatePages({
		currentPage, innerLimit, outerLimit, pagesQuantity,
	}), [currentPage, innerLimit, outerLimit, pagesQuantity]);

	return (
		<Stack as="ol" isInline spacing={1} {...props}>
			{pages.map((page, index) => <Page key={`${page}-${index}`} page={page} />)}
		</Stack>
	);
}
